import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { useTranslation } from "react-i18next";
import ErrorPage from "../../../Components/GeneralComponents/errorPage/ErrorPage";
import useFetchObjects from "../../../api/useFetchObjects";
import { useAuth } from "../../../context";
import formatDateTime from "../../../Components/GeneralComponents/DateFormatter/FormatDateTime";
import { useNavigate } from "react-router-dom";

import EditHtPatientProcedure from "../HTPatient/Procedure/EditHtPatientProcedure";
import EditProcedure from "../Beauty/Procedure/EditProcedure";
const tableCellStyle = {
  border: "1px solid #ddd",
  textAlign: "center",
  padding: "4px",
  fontSize: "0.8rem",
};

const getRowStyle = (record) => {
  // Optionally, add conditional styling based on fields.
  return {};
};

const InProgress = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const [recordId, setRecordId] = useState("");
  const [openEdit, setOpenEdit] = useState(null);
  const [recordType, setRecordType] = useState("");
  const navigate = useNavigate();
  // Local state for search and pagination.
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);

  // Fetch in-progress procedures (records with next_visit_date = today and status=3).
  const {
    data: inprogressData,
    isLoading,
    isError,
    refetch,
  } = useFetchObjects(
    ["in-progress", page, rowsPerPage, keyword],
    `in-progress/?search=${keyword}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [keyword, page, rowsPerPage, refetch]);

  useEffect(() => {
    if (inprogressData && inprogressData.count !== undefined) {
      setTotalRecords(inprogressData.count);
    }
  }, [inprogressData]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Sort records (if needed). Here we simply sort descending by the date.
  const sortedRecords = inprogressData?.results
    ? [...inprogressData.results].sort(
        (a, b) => new Date(b.date) - new Date(a.date)
      )
    : [];

  return (
    <Grid container justifyContent="center">
      <Paper sx={{ p: 2, width: "100%", borderRadius: 2, boxShadow: 3 }}>
        {/* Page Title */}
        <Typography
          variant="h5"
          fontWeight="bold"
          align="center"
          sx={{ pb: 2, borderBottom: "5px solid #007471" }}
        >
          {t("call_center.inProgressProcedures", "Today's Appointments")}
        </Typography>

        {/* Filter Panel */}
        <Paper
          sx={{
            p: 2,
            mt: 1,
            mb: 2,
            borderRadius: 2,
            backgroundColor: "#f1f1f1",
            boxShadow: 1,
          }}
          variant="outlined"
        >
          <Grid container spacing={3} alignItems="center">
            {/* Search Field */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth variant="outlined" size="medium">
                <TextField
                  label={t("call_center.search")}
                  variant="outlined"
                  size="medium"
                  value={keyword}
                  onChange={(e) => {
                    setKeyword(e.target.value);
                    setPage(1);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Paper>

        {/* Table Section */}
        <TableContainer sx={{ mt: 3 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={5}>
              <CircularProgress />
            </Box>
          ) : isError ? (
            <ErrorPage />
          ) : sortedRecords.length === 0 ? (
            <Typography align="center" variant="h6">
              {t("call_center.noResults", "No Record Found")}
            </Typography>
          ) : (
            <Table stickyHeader size="small">
              <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell sx={tableCellStyle}>
                    {t("call_center.no")}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {t("call_center.patient")}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {t("call_center.contact")}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {t("call_center.address")}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {t("call_center.procedureType")}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {t("call_center.date")}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {t("call_center.procedureDay")}
                  </TableCell>

                  <TableCell sx={tableCellStyle}>
                    {t("call_center.action")}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {t("call_center.nextVisitDate")}
                  </TableCell>
                  <TableCell sx={tableCellStyle}>
                    {t("call_center.recordType")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRecords.map((record, i) => (
                  <TableRow key={record.id} hover style={getRowStyle(record)}>
                    <TableCell sx={tableCellStyle}>{i + 1}</TableCell>
                    <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                      {record.patient}
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                      {record.phone}
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                      {record?.patient_address}
                    </TableCell>
                    <TableCell sx={{ ...tableCellStyle, fontWeight: "bold" }}>
                      {record.procedure_type}
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                      {record.date ? formatDateTime(record.date) : ""}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyle,
                        fontWeight: "bold",
                        textTransform: "capitalize",
                      }}
                    >
                      {record.procedure_day}
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                      <Button
                        variant="contained"
                        color="success"
                        sx={{ textTransform: "capitalize" }}
                        onClick={() => {
                          setRecordType(record?.record_type);
                          setRecordId(record?.id);
                          setOpenEdit(true);
                        }}
                      >
                        {t("call_center.followUp", "Follow Up")}
                      </Button>
                    </TableCell>
                    <TableCell sx={tableCellStyle}>
                      {record.next_visit_date
                        ? new Date(record.next_visit_date).toLocaleDateString()
                        : ""}
                    </TableCell>
                    <TableCell
                      sx={{ ...tableCellStyle, textTransform: "capitalize" }}
                    >
                      {record.record_type}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>

        {/* Pagination Controls */}
        <Box
          mt={2}
          mb={1}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Typography>{t("call_center.rowsPerPage")}:</Typography>
            <FormControl size="small">
              <Select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={500}>500</MenuItem>
                <MenuItem value={1000}>1000</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Pagination
            count={Math.ceil(totalRecords / rowsPerPage)}
            page={page}
            onChange={handlePageChange}
            variant="outlined"
            color="primary"
          />
        </Box>
      </Paper>

      <Dialog
        open={openEdit}
        onClose={() => {
          setOpenEdit(false);
        }}
        maxWidth={"lg"}
      >
        <DialogActions>
          <DialogContent>
            {recordId && recordType === "ht" && (
              <EditHtPatientProcedure id={recordId} />
            )}

            {recordId && recordType === "procedure" && (
              <EditProcedure id={recordId} />
            )}
          </DialogContent>
        </DialogActions>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEdit(false);
            }}
          >
            {t("call_center.close", "Close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default InProgress;
